import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Star, Flag, Mail, Phone, MapPin, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../../context/AuthContext";
import { STATUSES, statusCls, LANG_LABELS, fmtDate } from "./Applications";

const LANG_NAMES = { en: "English", es: "Spanish", fr: "French", pt: "Portuguese" };

const Card = ({ title, children, testid }) => (
  <div
    data-testid={testid}
    className="border border-white/10 rounded-xl p-5 md:p-6 bg-white/[0.02]"
  >
    <p className="text-[11px] uppercase tracking-[0.25em] text-secondary mb-4">
      {title}
    </p>
    {children}
  </div>
);

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const remove = async () => {
    if (
      !window.confirm(
        `Delete the application from ${data?.application?.stage_name}? This cannot be undone.`
      )
    )
      return;
    try {
      await apiFetch(`/admin/applications/${id}`, { method: "DELETE" });
      toast.success("Application deleted");
      navigate("/admin/applications");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const load = async () => {
    try {
      setData(await apiFetch(`/admin/applications/${id}`));
    } catch (e) {
      toast.error("Could not load this application.");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const patch = async (body) => {
    setSaving(true);
    try {
      await apiFetch(`/admin/applications/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await apiFetch(`/admin/applications/${id}/notes`, {
        method: "POST",
        body: JSON.stringify({ text: note.trim() }),
      });
      setNote("");
      await load();
      toast.success("Note added");
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!data) {
    return (
      <p className="text-sm text-muted-foreground uppercase tracking-[0.2em] animate-pulse py-16 text-center">
        Loading artist
      </p>
    );
  }

  const a = data.application;
  const links = [
    ["Best link", a.best_link],
    ["Additional link", a.additional_link],
    ["Instagram", a.instagram],
    ["TikTok", a.tiktok],
    ["YouTube", a.youtube],
    ["Spotify / Apple Music", a.spotify],
  ].filter(([, v]) => v);

  const flagBtn = (active, color) =>
    `inline-flex items-center gap-2 px-4 py-2 rounded-full border text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors duration-300 ${
      active
        ? color
        : "border-white/15 text-muted-foreground hover:border-white/40 hover:text-foreground"
    }`;

  return (
    <div data-testid="admin-application-detail">
      <Link
        to="/admin/applications"
        data-testid="admin-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft size={14} /> Applications
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 mb-8">
        <div>
          <h1 className="font-display font-bold tracking-tighter text-3xl md:text-5xl">
            {a.stage_name}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {a.full_name} · {a.artist_type} · {LANG_NAMES[a.language] || a.language} application ·{" "}
            {fmtDate(a.created_at)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <button
            data-testid="admin-flag-high-potential"
            disabled={saving}
            onClick={() => patch({ high_potential: !a.high_potential })}
            className={flagBtn(a.high_potential, "bg-primary/15 border-primary text-primary")}
          >
            <Star size={14} className={a.high_potential ? "fill-primary" : ""} />
            High potential
          </button>
          <button
            data-testid="admin-flag-follow-up"
            disabled={saving}
            onClick={() => patch({ follow_up: !a.follow_up })}
            className={flagBtn(a.follow_up, "bg-secondary/15 border-secondary text-secondary")}
          >
            <Flag size={14} className={a.follow_up ? "fill-secondary" : ""} />
            Follow up
          </button>
          <select
            data-testid="admin-status-select"
            value={a.status}
            disabled={saving}
            onChange={(e) => patch({ status: e.target.value })}
            className={`bg-white/5 border rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] focus:outline-none [&>option]:bg-[#0a0a0a] ${statusCls(a.status)}`}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            data-testid="admin-delete-application"
            onClick={remove}
            aria-label="Delete application"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-muted-foreground text-[11px] font-semibold uppercase tracking-[0.12em] hover:border-primary hover:text-primary transition-colors duration-300"
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Contact" testid="admin-detail-contact">
          <div className="space-y-2.5 text-sm">
            <a href={`mailto:${a.email}`} className="flex items-center gap-3 hover:text-primary transition-colors break-all min-w-0">
              <Mail size={15} className="text-muted-foreground shrink-0" /> {a.email}
            </a>
            {a.phone && (
              <p className="flex items-center gap-3">
                <Phone size={15} className="text-muted-foreground" /> {a.phone}
              </p>
            )}
            <p className="flex items-center gap-3">
              <MapPin size={15} className="text-muted-foreground" /> {a.city} · {a.age} years old
            </p>
          </div>
        </Card>

        <Card title="Audition links" testid="admin-detail-links">
          <div className="space-y-2.5">
            {links.map(([label, url]) => (
              <a
                key={label}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`admin-link-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="flex items-center justify-between gap-3 text-sm group border border-white/10 rounded-lg px-4 py-2.5 hover:border-primary/50 transition-colors"
              >
                <span className="truncate min-w-0">
                  <span className="text-muted-foreground text-xs uppercase tracking-[0.1em] mr-2">
                    {label}
                  </span>
                  {url}
                </span>
                <ArrowUpRight size={15} className="shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
              </a>
            ))}
          </div>
        </Card>

        <Card title="Coachability" testid="admin-detail-responses">
          <div className="space-y-4 text-sm">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">
                Why grow as an artist
              </p>
              <p className="font-light whitespace-pre-wrap">{a.motivation}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">
                Approach to feedback
              </p>
              <p className="font-light whitespace-pre-wrap">{a.feedback}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">
                12-month commitment
              </p>
              <p className="font-semibold text-secondary">{a.commitment}</p>
            </div>
          </div>
        </Card>

        <Card title="Internal notes" testid="admin-detail-notes">
          <form onSubmit={addNote} className="flex gap-2 mb-4">
            <input
              data-testid="admin-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a private note..."
              className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary/60 transition-colors"
            />
            <button
              type="submit"
              data-testid="admin-note-submit"
              className="bg-primary text-white rounded-md px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] hover:brightness-110 transition-[filter]"
            >
              Add
            </button>
          </form>
          <div className="space-y-3">
            {(a.notes || []).length === 0 && (
              <p className="text-sm text-muted-foreground">No notes yet.</p>
            )}
            {[...(a.notes || [])].reverse().map((n) => (
              <div key={n.id} data-testid={`admin-note-${n.id}`} className="border-l-2 border-primary/40 pl-3">
                <p className="text-sm font-light whitespace-pre-wrap">{n.text}</p>
                <p className="text-[11px] text-muted-foreground mt-1 uppercase tracking-[0.1em]">
                  {n.admin_name} · {fmtDate(n.created_at)}
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card title="Activity" testid="admin-activity">
        <div className="mt-1 space-y-3">
          {data.activity.length === 0 && (
            <p className="text-sm text-muted-foreground">No activity yet.</p>
          )}
          {data.activity.map((ev) => (
            <div key={ev.id} className="flex flex-wrap items-baseline gap-x-3 text-sm">
              <span className="font-semibold uppercase tracking-[0.1em] text-xs">
                {ev.action}
              </span>
              {ev.detail && (
                <span className="text-muted-foreground text-xs">{ev.detail}</span>
              )}
              <span className="text-[11px] uppercase tracking-[0.1em] text-secondary">
                By: {ev.admin_name}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {fmtDate(ev.created_at)}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
